import { getStroke } from 'perfect-freehand';

// YouTube / PDF URL utilities
export function extractYouTubeId(url) {
  const patterns = [
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/,
    /youtube\.com\/embed\/([^"&?/\s]{11})/,
    /youtube\.com\/v\/([^"&?/\s]{11})/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export function isYouTubeLink(url) {
  return url.includes('youtube.com') || url.includes('youtu.be');
}

export function getYouTubeId(url) {
  return extractYouTubeId(url);
}

// eslint-disable-next-line no-unused-vars
export function isPdfUrl(url) {
  return url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('.pdf');
}

// Board constants
export const PEN_COLORS = [
  '#222', '#8a2be2', '#f59e42', '#e11d48', '#22c55e', '#2563eb', '#fbbf24', '#fff', '#000',
];
export const GRID_SIZE = 32;
export const BOARD_WIDTH = 12000;
export const BOARD_HEIGHT = 12000;
export const STORAGE_KEY = 'miro_whiteboard_v2';

// Pointer coordinate translation (screen → board space)
export function getPointerCoords(e, pan, svgRef) {
  const rect = svgRef.current.getBoundingClientRect();
  let clientX, clientY;
  if (e.touches && e.touches.length > 0) {
    clientX = e.touches[0].clientX;
    clientY = e.touches[0].clientY;
  } else {
    clientX = e.clientX;
    clientY = e.clientY;
  }
  const safeScale = Math.max(pan.scale, 0.1);
  const x = (clientX - rect.left - pan.x) / safeScale;
  const y = (clientY - rect.top - pan.y) / safeScale;
  return { x, y };
}

// Grid drawing
export function drawDottedGrid(ctx, w, h, gridSize, offsetX, offsetY, scale) {
  ctx.clearRect(0, 0, w, h);
  ctx.save();
  ctx.fillStyle = '#e6e6ee';
  ctx.shadowBlur = 0;
  ctx.setTransform(scale, 0, 0, scale, offsetX, offsetY);
  const dotRadius = 1.5;
  for (let x = (-offsetX / scale) % gridSize; x < w / scale; x += gridSize) {
    for (let y = (-offsetY / scale) % gridSize; y < h / scale; y += gridSize) {
      ctx.beginPath();
      ctx.arc(x, y, dotRadius, 0, 2 * Math.PI);
      ctx.fill();
    }
  }
  ctx.restore();
}

// Freehand stroke utilities (perfect-freehand)
const average = (a, b) => (a + b) / 2;

export function getSvgPathFromStroke(points, closed = true) {
  if (points.length < 4) return '';

  let a = points[0];
  let b = points[1];
  const c = points[2];

  let result = `M${a[0].toFixed(2)},${a[1].toFixed(2)} Q${b[0].toFixed(2)},${b[1].toFixed(2)} ${average(b[0], c[0]).toFixed(2)},${average(b[1], c[1]).toFixed(2)} T`;

  for (let i = 2, max = points.length - 1; i < max; i++) {
    a = points[i];
    b = points[i + 1];
    result += `${average(a[0], b[0]).toFixed(2)},${average(a[1], b[1]).toFixed(2)} `;
  }

  if (closed) result += 'Z';
  return result;
}

export function createSmoothPath(points, strokeWidth = 2) {
  if (points.length < 2) return '';
  const stroke = getStroke(points, {
    size: strokeWidth * 2,
    thinning: 0.5,
    smoothing: 0.5,
    streamline: 0.5,
    simulatePressure: true,
  });
  if (stroke.length === 0) return '';
  return getSvgPathFromStroke(stroke);
}

// Eraser geometry utilities
export function distanceFromPointToLineSegment(x1, y1, x2, y2, px, py) {
  const A = px - x1, B = py - y1, C = x2 - x1, D = y2 - y1;
  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = lenSq !== 0 ? dot / lenSq : -1;
  let xx, yy;
  if (param < 0) { xx = x1; yy = y1; }
  else if (param > 1) { xx = x2; yy = y2; }
  else { xx = x1 + param * C; yy = y1 + param * D; }
  return Math.sqrt((px - xx) ** 2 + (py - yy) ** 2);
}

export function isLineIntersectingEraser(linePoints, eraserX, eraserY, eraserSize) {
  for (let i = 0; i < linePoints.length - 1; i++) {
    const [x1, y1] = linePoints[i];
    const [x2, y2] = linePoints[i + 1];
    if (distanceFromPointToLineSegment(x1, y1, x2, y2, eraserX, eraserY) <= eraserSize / 2) {
      return true;
    }
  }
  return false;
}

// Reducer (kept for reference, not currently used by main component)
export const initialState = { shapes: [], history: [[]], historyIndex: 0 };

export function reducer(state, action) {
  switch (action.type) {
    case 'ADD_SHAPE': {
      const newShapes = [...state.shapes, action.payload];
      const newHistory = state.history.slice(0, state.historyIndex + 1);
      newHistory.push(newShapes);
      return { ...state, shapes: newShapes, history: newHistory, historyIndex: newHistory.length - 1 };
    }
    case 'UNDO':
      if (state.historyIndex > 0) {
        return { ...state, shapes: [...state.history[state.historyIndex - 1]], historyIndex: state.historyIndex - 1 };
      }
      return state;
    default:
      return state;
  }
}

export function shouldIgnoreKeyboardEvent(e) {
  const activeElement = document.activeElement;
  if (!activeElement) return false;
  if (activeElement.classList && activeElement.classList.contains('lesson-title-input')) return true;
  if (activeElement.closest && activeElement.closest('.lesson-title-input')) return true;
  if (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') return true;
  if (activeElement.isContentEditable) return true;
  return false;
}
